/**
 * Zeitverteilung (F-28): Wie viel Zeit geht in welchen Lebensbereich, wie
 * viel Schlaf, wie viel bleibt unverplant?
 *
 * Regeln (rein, getestet):
 * - Je Tag zaehlt nur der Anteil eines Termins in diesem Tag (Mitternacht,
 *   Zeitraumgrenzen). Ganztaegige Termine zaehlen nicht.
 * - Ueberschneiden sich Termine, teilen sie sich die Zeit zu gleichen
 *   Teilen - so ist die verplante Zeit nie groesser als die belegte.
 * - Lernzeit aus der Stoppuhr zaehlt zum Schul-Bereich, aber nur, wo kein
 *   Termin liegt.
 * - Tatsaechlich (Standard): vergangene Termine nach F-29 - ausgefallene
 *   zaehlen nicht, "anders gelaufen" mit der eingetragenen Zeit,
 *   unbestaetigte vorlaeufig wie geplant. Geplant: alle Termine wie
 *   eingetragen, ohne Lernzeit.
 * - Schlaf aus dem Morgenjournal, sonst der Standardwert ("geschaetzt").
 * - Frei = Tageslaenge - Schlaf - verplant; negativ heisst ueberplant.
 *   Die Tageslaenge ist die echte (23 bzw. 25 h bei der Zeitumstellung).
 */
import { tatsaechlicheZeit, zustand } from '@/domain/calendar/bestaetigung';
import { addDays, minutesOfDay, tagesMinuten } from '@/lib/datetime';
import type { CalendarOccurrence, ID, LocalDate, LocalDateTime } from '@/domain/types';

export type ZeitModus = 'tatsaechlich' | 'geplant';

export const OHNE_BEREICH = 'ohne';

export interface ZeitEingabe {
  tage: LocalDate[];
  termine: CalendarOccurrence[];
  /** Stoppuhr-Lernzeiten (F-22) als lokale Zeiten. */
  lernzeiten: { start: LocalDateTime; end: LocalDateTime }[];
  schuleBereich: ID | null;
  /** Schlafdauer in Stunden je Tag (Morgenjournal des Tages). */
  schlaf: Map<LocalDate, number>;
  standardSchlaf: number;
  jetzt: LocalDateTime;
  modus: ZeitModus;
  /** Nur fuer Tests: Tageslaenge in Minuten. */
  tagLaenge?: (tag: LocalDate) => number;
}

export interface BereichsZeit {
  key: string;
  areaId: ID | null;
  minuten: number;
  /** Was dazu beitraegt - fuer die Liste beim Klick auf ein Segment. */
  termine: { occ: CalendarOccurrence; minuten: number }[];
  lernzeit: number;
}

export interface ZeitVerteilung {
  tage: number;
  /** Summe der Tageslaengen in Minuten. */
  gesamt: number;
  /** Absteigend nach Minuten. */
  bereiche: BereichsZeit[];
  verplant: number;
  schlaf: number;
  /** An so vielen Tagen fehlte der Schlaf im Journal. */
  schlafGeschaetzt: number;
  /**
   * Weder verplant noch Schlaf. Bewusst nicht "Freizeit" (F-49): fuer Ole ist
   * unverplante Zeit keine gewollte Freizeit - echte Erholung ist ein Bereich.
   */
  unverplant: number;
  ueberplant: number;
  ueberplanteTage: LocalDate[];
  /** Vergangene, noch unbestaetigte Termine im Zeitraum (F-29). */
  unbestaetigt: number;
}

interface Stueck { a: number; b: number; key: string; occ?: CalendarOccurrence }

/** Teil von [start, end) innerhalb des Tags, in Minuten ab Mitternacht. */
function imTag(start: LocalDateTime, end: LocalDateTime, tag: LocalDate): [number, number] | null {
  const von = `${tag}T00:00`;
  const bis = `${addDays(tag, 1)}T00:00`;
  const s = start < von ? von : start;
  const e = end > bis ? bis : end;
  if (s >= e) return null;
  return [minutesOfDay(s), e === bis ? 1440 : minutesOfDay(e)];
}

export function zeitverteilung(ein: ZeitEingabe): ZeitVerteilung {
  const laenge = ein.tagLaenge ?? tagesMinuten;
  const bereiche = new Map<string, BereichsZeit>();
  const bereich = (key: string, areaId: ID | null) => {
    let b = bereiche.get(key);
    if (!b) { b = { key, areaId, minuten: 0, termine: [], lernzeit: 0 }; bereiche.set(key, b); }
    return b;
  };
  const beitrag = new Map<string, { occ: CalendarOccurrence; minuten: number }>();
  const schuleKey = ein.schuleBereich ?? OHNE_BEREICH;

  // Zeiten je Termin einmal bestimmen.
  const zeiten = ein.termine.flatMap((o) => {
    if (o.allDay) return [];
    const z = ein.modus === 'geplant'
      ? { start: o.start as LocalDateTime, end: o.end as LocalDateTime }
      : tatsaechlicheZeit(o, ein.jetzt);
    return z ? [{ o, ...z }] : [];
  });

  let gesamt = 0, verplant = 0, schlaf = 0, schlafGeschaetzt = 0, unverplant = 0, ueberplant = 0;
  const ueberplanteTage: LocalDate[] = [];

  for (const tag of ein.tage) {
    const tagMin = laenge(tag);
    const stuecke: Stueck[] = [];
    for (const z of zeiten) {
      const t = imTag(z.start, z.end, tag);
      if (t) stuecke.push({ a: t[0], b: t[1], key: z.o.areaId ?? OHNE_BEREICH, occ: z.o });
    }
    const lernen: [number, number][] = ein.modus === 'geplant' ? [] : ein.lernzeiten.flatMap((l) => {
      const t = imTag(l.start, l.end, tag);
      return t ? [t] : [];
    });

    // Sweep ueber alle Grenzen: Termine teilen sich die Zeit, Lernzeit fuellt Luecken.
    const grenzen = [...new Set([...stuecke.flatMap((s) => [s.a, s.b]), ...lernen.flat()])].sort((x, y) => x - y);
    let tagVerplant = 0;
    for (let i = 0; i < grenzen.length - 1; i++) {
      const p = grenzen[i]!, q = grenzen[i + 1]!, dauer = q - p;
      const aktiv = stuecke.filter((s) => s.a <= p && s.b >= q);
      if (aktiv.length > 0) {
        const anteil = dauer / aktiv.length;
        for (const s of aktiv) {
          bereich(s.key, s.occ!.areaId ?? null).minuten += anteil;
          const k = s.occ!.occurrenceId;
          const alt = beitrag.get(k);
          if (alt) alt.minuten += anteil; else beitrag.set(k, { occ: s.occ!, minuten: anteil });
        }
        tagVerplant += dauer;
      } else if (lernen.some(([a, b]) => a <= p && b >= q)) {
        const b = bereich(schuleKey, ein.schuleBereich);
        b.minuten += dauer;
        b.lernzeit += dauer;
        tagVerplant += dauer;
      }
    }

    const s = ein.schlaf.get(tag);
    if (s === undefined) schlafGeschaetzt++;
    const schlafMin = Math.round((s ?? ein.standardSchlaf) * 60);
    const rest = tagMin - schlafMin - tagVerplant;
    if (rest < 0) { ueberplant += -rest; ueberplanteTage.push(tag); }
    gesamt += tagMin;
    verplant += tagVerplant;
    schlaf += schlafMin;
    unverplant += Math.max(0, rest);
  }

  for (const { occ, minuten } of beitrag.values()) bereich(occ.areaId ?? OHNE_BEREICH, occ.areaId ?? null).termine.push({ occ, minuten });
  for (const b of bereiche.values()) b.termine.sort((x, y) => String(x.occ.start).localeCompare(String(y.occ.start)));

  const erster = ein.tage[0] ?? '';
  const naechster = ein.tage.length > 0 ? addDays(ein.tage[ein.tage.length - 1]!, 1) : '';
  const unbestaetigt = ein.termine.filter((o) => String(o.start) >= erster && String(o.start) < naechster && zustand(o, ein.jetzt).zustand === 'offen').length;

  return {
    tage: ein.tage.length,
    gesamt,
    bereiche: [...bereiche.values()].filter((b) => b.minuten > 0).sort((x, y) => y.minuten - x.minuten),
    verplant, schlaf, schlafGeschaetzt, unverplant, ueberplant, ueberplanteTage, unbestaetigt,
  };
}

/** Minuten als "5,5 h" (auf halbe Stunden genau unter 10 h, sonst ganze). */
export function stunden(min: number): string {
  const h = min / 60;
  const gerundet = h < 10 ? Math.round(h * 2) / 2 : Math.round(h);
  return `${gerundet.toLocaleString('de-DE')} h`;
}

/**
 * Wunschverteilung (F-28): Zielzeit eines Bereichs, umgerechnet auf den
 * Zeitraum (Woche voll, Tag 1/7, Monat Tage/7), und ob sie erreicht ist.
 * Ohne Ziel `null`.
 */
export function zielStand(minuten: number, zielStundenWoche: number | undefined, tage: number): { ziel: number; erreicht: boolean; fehlt: number } | null {
  if (!zielStundenWoche || zielStundenWoche <= 0 || tage <= 0) return null;
  const ziel = (zielStundenWoche * 60 * tage) / 7;
  // Eine Minute Rundungsspielraum - 4:59 h bei 5 h Ziel gilt nicht als verfehlt.
  const fehlt = Math.max(0, ziel - minuten);
  return { ziel, erreicht: fehlt <= 1, fehlt };
}
